import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Check, CreditCard, Phone, Smartphone, Trash2 } from 'lucide-react'
import { RotatingLines } from 'react-loader-spinner'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { AppState } from '../../state'
import {
	useCreateServiceOrderItem,
	useSwitchPage,
	useUpdateCart,
} from '../../state/features/applicationSlice/hooks'
import { ApplicationPages, setCart } from '../../state/features/applicationSlice'
import { DDGColors } from '../../constants/colors'
import Image from '../Image'
import { convertDateToString, formatAddress, formatNumberScale } from '../../functions'
import { db } from '../../config/firebase'
import { doc, onSnapshot } from 'firebase/firestore'
import { bookingsCollections } from '../../constants/firebase'
import { axiosClient } from '../../config/axiosClient'

type SelectedMethod = {
	id: string
	type: 'card' | 'mobile_money'
	value?: string
} | null

const MOMO_PROVIDERS = [
	{ id: 'mtn', label: 'MTN MoMo', logo: 'https://www.mtn.com/wp-content/uploads/2022/12/momo-logo-horiz.png' },
	{ id: 'atl', label: 'AirtelTigo', logo: 'https://amaghanaonline.com/wp-content/uploads/2022/07/WhatsApp-Image-2022-07-27-at-5.16.26-PM.jpeg' },
	{ id: 'vod', label: 'Telecel Cash', logo: 'https://play.telecel.com.gh/static/Rede-5f0f780acc6c05a6539d7e3229ac508c.webp' },
	{ id: 'gmoney', label: 'G-Money', logo: 'https://gm-shuttle.gmfs.africa/web_app/assets/brand/gmoney_light.png' },
]

const PENDING_SERVICE_KEY = 'ddg_widget_pending_service'

const ServiceBookingReview: React.FC = () => {
	const { User, Account, PaymentMethods } = useSelector<AppState, AppState['auth']>((s) => s.auth)
	const { cart, activeFacility, institutionLocations } = useSelector<AppState, AppState['application']>((s) => s.application)
	const router = useSwitchPage()
	const orderFunc = useCreateServiceOrderItem()
	const updateCart = useUpdateCart()
	const dispatch = useDispatch()

	// Booking details
	const [loading, setLoading] = useState(false)
	const [locationId, setLocationId] = useState<string>('')
	const [bookingDate, setBookingDate] = useState<string>(new Date().toISOString().split('T')[0])
	const [bookingTime, setBookingTime] = useState<string>('')

	// Payment method
	const [selectedMethod, setSelectedMethod] = useState<SelectedMethod>(null)
	const [momoPhone, setMomoPhone] = useState('')
	const [savedCardsOpen, setSavedCardsOpen] = useState(false)

	// Payment flow
	const [bookingId, setBookingId] = useState<string | null>(null)
	const [awaitingPayment, setAwaitingPayment] = useState(false)
	const [payReference, setPayReference] = useState<string | null>(null)
	const [paymentMessage, setPaymentMessage] = useState('')
	const [pendingPrId, setPendingPrId] = useState<string | null>(null)
	const [manualVerifying, setManualVerifying] = useState(false)
	const [timeLeft, setTimeLeft] = useState(300)
	const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

	// OTP
	const [showOtp, setShowOtp] = useState(false)
	const [otpInput, setOtpInput] = useState('')
	const [submittingOtp, setSubmittingOtp] = useState(false)

	// Firestore listener — fires when payment webhook confirms the booking
	useEffect(() => {
		if (!bookingId || !awaitingPayment) return
		const ref = doc(db, bookingsCollections, bookingId)
		const unsub = onSnapshot(ref, (snap) => {
			if (!snap.exists()) return
			const b = snap.data() as any
			if (b.paymentStatus === 'PAID') {
				if (timerRef.current) clearInterval(timerRef.current)
				setAwaitingPayment(false)
				setLoading(false)
				sessionStorage.removeItem(PENDING_SERVICE_KEY)
				dispatch(setCart(null))
				toast.success('Service successfully booked!')
				router(ApplicationPages.BOOKING_SERVICE_SUCCESS)
			} else if (b.status === 'CANCELED') {
				if (timerRef.current) clearInterval(timerRef.current)
				setAwaitingPayment(false)
				setLoading(false)
				toast.error("We received your payment but couldn't confirm the booking. Funds returned to wallet.")
			}
		})
		return () => { unsub(); if (timerRef.current) clearInterval(timerRef.current) }
	}, [bookingId, awaitingPayment])

	// 5-minute countdown
	useEffect(() => {
		if (!manualVerifying) return
		timerRef.current = setInterval(() => {
			setTimeLeft((s) => {
				if (s <= 1) {
					clearInterval(timerRef.current!)
					setManualVerifying(false)
					setAwaitingPayment(false)
					setLoading(false)
					toast.error('Payment confirmation timed out. Contact support if payment was deducted.')
					return 0
				}
				return s - 1
			})
		}, 1000)
		return () => { if (timerRef.current) clearInterval(timerRef.current) }
	}, [manualVerifying])

	// Handle return from card redirect
	useEffect(() => {
		const trxref = new URLSearchParams(window.location.search).get('trxref')
		if (!trxref) return
		const pending = sessionStorage.getItem(PENDING_SERVICE_KEY)
		if (!pending) return
		try {
			const { bookingId: bId } = JSON.parse(pending)
			setBookingId(bId)
			setManualVerifying(true)
			setTimeLeft(300)
			setAwaitingPayment(true)
		} catch {}
	}, [])

	const formatTimer = (s: number) => {
		const m = Math.floor(s / 60)
		const sec = s % 60
		return `${m}:${sec < 10 ? '0' : ''}${sec}`
	}

	const startPaymentForRequest = async (prId: string, bId: string) => {
		setPendingPrId(prId)
		const isMomo = selectedMethod?.type === 'mobile_money'
		try {
			if (isMomo) {
				const { data } = await axiosClient.post('/payalt/init-payment-request', {
					paymentRequestId: prId,
					channel: 'mobile_money',
					phone: selectedMethod!.value,
					provider: selectedMethod!.id,
				})
				const { status, reference, display_text } = data?.data ?? {}
				if (reference) setPayReference(reference)
				setLoading(false)
				if (status === 'send_otp') {
					setShowOtp(true)
					setAwaitingPayment(true)
				} else {
					setPaymentMessage(display_text ?? 'Please complete the payment on your mobile device')
					setAwaitingPayment(true)
				}
			} else {
				const { data } = await axiosClient.post('/payalt/init-payment-request', {
					paymentRequestId: prId,
					channel: 'card',
				})
				sessionStorage.setItem(PENDING_SERVICE_KEY, JSON.stringify({ bookingId: bId }))
				window.location.href = data?.data?.authorization_url
			}
		} catch (err: any) {
			setLoading(false)
			setAwaitingPayment(false)
			toast.error(err?.response?.data?.message ?? 'Unable to start payment')
		}
	}

	const handleOtpSubmit = async () => {
		if (!otpInput || !payReference) return
		setSubmittingOtp(true)
		try {
			const { data } = await axiosClient.post('/payalt/verify-otp', {
				data: { otp: otpInput, reference: payReference },
				payPartner: 'paystack',
				patientId: Account?.uid,
			})
			if (data?.data?.paymentStatus === 'success' || data?.status === 'Success') {
				setShowOtp(false)
				setManualVerifying(true)
				setTimeLeft(300)
			} else {
				toast.error('OTP verification failed. Please try again.')
			}
		} catch (err: any) {
			toast.error(err?.response?.data?.message ?? 'OTP verification failed')
		} finally {
			setSubmittingOtp(false)
		}
	}

	const continueFunc = async () => {
		if (loading) return
		if (!selectedMethod && !activeFacility?.paymentConfig?.accepts_payment_on_delivery) {
			toast.error('Please select a payment method')
			return
		}
		if (selectedMethod?.type === 'mobile_money' && (!selectedMethod.value || selectedMethod.value.trim().length < 9)) {
			toast.error('Please enter a valid phone number')
			return
		}
		setLoading(true)
		orderFunc(
			{
				items: cart,
				patientMemberId: User?.memberID ?? '',
				patientId: User?.accountId ?? '',
				paymentMethod: activeFacility?.paymentConfig?.accepts_payment_on_delivery
					? 'CASH'
					: (selectedMethod?.type as any) ?? 'mobile_money',
				ignorePayment: activeFacility?.paymentConfig?.accepts_payment_on_delivery ?? false,
				fees: {
					serviceFee: activeFacility?.paymentConfig?.accepts_payment_on_delivery ? 0 : 15,
					deliveryFee: 0,
				},
				deliveryMethod: 'ON-SITE',
				locationId,
				startTime: new Date(bookingDateTime ?? 0),
				facilityId: activeFacility?.id ?? '',
			},
			async (response: any) => {
				const resData = response?.data ?? response
				const { bookingId: bId, requiresPayment, paymentRequestId } = resData ?? {}
				if (requiresPayment && paymentRequestId && bId) {
					setBookingId(bId)
					await startPaymentForRequest(paymentRequestId, bId)
				} else if (bId) {
					setLoading(false)
					dispatch(setCart(null))
					toast.success('Service successfully booked!')
					router(ApplicationPages.BOOKING_SERVICE_SUCCESS)
				} else {
					setLoading(false)
					toast.error(response?.message ?? 'Error placing order')
				}
			}
		)
	}

	const bookingDateTime = useMemo(() => {
		if (!bookingDate || !bookingTime) return null
		const [h, m] = bookingTime.split(':').map(Number)
		const d = new Date(bookingDate)
		d.setHours(h, m)
		return d.getTime()
	}, [bookingDate, bookingTime])

	const orderAmount = useMemo(() => {
		return [...(cart ?? [])].reduce(
			(acc, val) =>
				acc +
				(activeFacility?.paymentConfig?.discount
					? val.price - val.price * activeFacility.paymentConfig.discount
					: val.price) * val.quantity,
			0,
		)
	}, [cart, activeFacility])

	if (!Account) return <></>

	// OTP screen
	if (awaitingPayment && showOtp) {
		return (
			<div className="flex flex-col flex-1 items-center justify-center p-6">
				<div className="bg-white rounded-2xl border border-gray-100 shadow p-6 w-full flex flex-col items-center gap-5">
					<div className="w-14 h-14 rounded-full bg-[#edfbf8] flex items-center justify-center">
						<Smartphone size={28} color="#034a47" />
					</div>
					<div className="text-center">
						<p className="text-base font-bold text-[#022B29] mb-1">Enter OTP</p>
						<p className="text-xs text-[#546E6C]">Enter the one-time password sent to your mobile money number.</p>
					</div>
					<input
						type="text"
						inputMode="numeric"
						maxLength={6}
						value={otpInput}
						onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, ''))}
						placeholder="• • • • • •"
						className="w-full text-center text-2xl font-bold tracking-[0.5em] border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-[#034a47]"
						autoFocus
					/>
					<div className="w-full flex flex-col gap-2">
						<button
							onClick={handleOtpSubmit}
							disabled={otpInput.length < 4 || submittingOtp}
							className="w-full py-3 bg-[#034a47] text-white rounded-full text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
						>
							{submittingOtp ? <RotatingLines width="16" strokeColor="#ffffff" /> : 'Verify OTP'}
						</button>
						<button
							onClick={() => { setShowOtp(false); setAwaitingPayment(false); setLoading(false) }}
							className="w-full py-3 border-2 border-gray-200 rounded-full text-sm font-semibold text-[#022B29]"
						>
							Cancel
						</button>
					</div>
				</div>
			</div>
		)
	}

	// Verifying countdown screen
	if (awaitingPayment && manualVerifying) {
		return (
			<div className="flex flex-col flex-1 items-center justify-center p-6">
				<div className="bg-white rounded-2xl border border-gray-100 shadow p-6 w-full flex flex-col items-center gap-5">
					<div className="relative w-20 h-20 flex items-center justify-center">
						<svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 80 80">
							<circle cx="40" cy="40" r="34" fill="none" stroke="#e4e2dd" strokeWidth="6" />
							<circle cx="40" cy="40" r="34" fill="none" stroke="#034a47" strokeWidth="6"
								strokeDasharray={`${2 * Math.PI * 34}`}
								strokeDashoffset={`${2 * Math.PI * 34 * (1 - timeLeft / 300)}`}
								strokeLinecap="round" className="transition-all duration-1000" />
						</svg>
						<span className="text-base font-bold text-[#022B29] font-mono">{formatTimer(timeLeft)}</span>
					</div>
					<div className="text-center">
						<p className="text-base font-bold text-[#022B29] mb-1">Verifying Payment…</p>
						<p className="text-xs text-[#546E6C]">Please wait while we confirm your payment.</p>
					</div>
					<div className="flex items-center gap-2 text-[#546E6C] text-xs">
						<RotatingLines width="16" strokeColor="#034a47" />
						<span>Listening for confirmation…</span>
					</div>
					<button
						onClick={() => { if (timerRef.current) clearInterval(timerRef.current); setManualVerifying(false); setAwaitingPayment(false); setLoading(false) }}
						className="text-xs text-[#546E6C] underline"
					>
						Cancel and go back
					</button>
				</div>
			</div>
		)
	}

	// Awaiting momo payment screen
	if (awaitingPayment) {
		return (
			<div className="flex flex-col flex-1 items-center justify-center p-6">
				<div className="bg-white rounded-2xl border border-gray-100 shadow p-6 w-full flex flex-col items-center gap-5">
					<div className="w-14 h-14 rounded-full bg-[#edfbf8] flex items-center justify-center">
						<Smartphone size={28} color="#034a47" />
					</div>
					<div className="text-center">
						<p className="text-base font-bold text-[#022B29] mb-1">Complete Payment on Your Phone</p>
						<p className="text-xs text-[#546E6C]">{paymentMessage}</p>
						{pendingPrId && (
							<a
								href={`https://app.drdogood.health/pay/${pendingPrId}`}
								target="_blank"
								rel="noopener noreferrer"
								className="text-xs text-[#034a47] underline mt-2 inline-block font-medium"
							>
								Open payment page →
							</a>
						)}
					</div>
					<div className="w-full flex flex-col gap-2">
						<button
							onClick={() => { setManualVerifying(true); setTimeLeft(300) }}
							className="w-full py-3 bg-[#034a47] text-white rounded-full text-sm font-semibold"
						>
							I&apos;ve Completed Payment
						</button>
						<button
							onClick={() => { setAwaitingPayment(false); setLoading(false) }}
							className="w-full py-3 border-2 border-gray-200 rounded-full text-sm font-semibold text-[#022B29]"
						>
							Cancel
						</button>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className="flex flex-col flex-1 space-y-4 overflow-y-scroll scroll">
			{/* Cart items */}
			<div className="px-2">
				<p className="text-[10px] font-semibold text-gray700">Your items</p>
				<div>
					{(cart ?? []).map((item, index) => (
						<div key={index} className={`flex flex-row items-center py-4 ${index === 0 ? '' : 'border-[#D6D6D6] border-opacity-50 border-t'}`}>
							<Image
								className="object-cover w-[54px] h-[54px] rounded-lg"
								useBase={false}
								src={Boolean(item.image) ? item.image! : activeFacility?.details.coverImage ?? '/img/surgery.png'}
								alt=""
							/>
							<div className="flex flex-col flex-1 px-3 space-y-1">
								<p className="text-sm font-bold">{item.name}</p>
								<p className="text-xs font-semibold">₵{formatNumberScale(Number(item.price))}</p>
							</div>
							{(cart ?? []).length > 1 && (
								<button onClick={() => updateCart([item as any])} className="p-3 rounded-full bg-lightBlood">
									<Trash2 color={DDGColors.blood} size={12} />
								</button>
							)}
						</div>
					))}
				</div>
			</div>

			{/* Location */}
			<div className="px-2">
				<div className="border-[#D6D6D6] border-opacity-50 border-b pb-4">
					<p className="text-[10px] font-semibold text-gray700">Select a Location</p>
					<p className="text-[#667085] text-xs mb-2">Select a convenient location for your service.</p>
					<select
						onChange={(e) => setLocationId(e.target.value)}
						value={locationId}
						className="border rounded-lg border-[#D0D5DD] outline-none text-xs w-full bg-transparent p-3 text-[#98A2B3] cursor-pointer mt-2"
					>
						<option value="" disabled>Select an option</option>
						{[...institutionLocations].map((item) => (
							<option key={item.id} value={item.id}>
								{item.locationName} — {formatAddress(item.address)}
							</option>
						))}
					</select>
				</div>
			</div>

			{/* Date & time */}
			<div className="px-2">
				<div className="border-[#D6D6D6] border-opacity-50 border-b py-4">
					<p className="text-[10px] font-semibold text-gray700">Select a Time</p>
					<p className="text-[#667085] text-xs mb-2">Select a date and time within our opening hours.</p>
					<div className="flex flex-row space-x-3">
						<input
							value={bookingDate}
							onChange={(e) => setBookingDate(e.target.value)}
							min={new Date().toISOString().split('T')[0]}
							type="date"
							className="border rounded-lg border-[#D0D5DD] outline-none text-xs w-full bg-transparent p-2 text-[#98A2B3] cursor-pointer"
						/>
						<input
							value={bookingTime}
							onChange={(e) => setBookingTime(e.target.value)}
							type="time"
							className="border rounded-lg border-[#D0D5DD] outline-none text-xs w-full bg-transparent p-2 text-[#98A2B3] cursor-pointer"
						/>
					</div>
				</div>
			</div>

			{/* Payment methods */}
			{!activeFacility?.paymentConfig?.accepts_payment_on_delivery && (
				<div className="px-2 flex flex-col gap-3">
					<p className="text-[10px] font-semibold text-gray700 uppercase tracking-wider">Payment method</p>

					{/* DDG Wallet — shown as info, auto-applied by backend */}
					<div className="flex items-center justify-between p-3 rounded-xl bg-[#f0faf9] border border-[#c8ece8]">
						<div>
							<p className="text-[10px] font-semibold text-[#546E6C] uppercase tracking-wider">DDG balance</p>
							<p className="text-base font-bold text-[#022B29]">₵{((Account.balance?.value ?? 0) + (Account.balance?.org_value ?? 0)).toFixed(2)}</p>
						</div>
						<Image useBase alt="wallet" src="/img/empty-wallet.svg" width={20} height={20} />
					</div>

					{/* Mobile Money */}
					<div className={`border-2 rounded-xl transition-all ${selectedMethod?.type === 'mobile_money' ? 'border-[#034a47]' : 'border-gray-200'}`}>
						<button
							onClick={() => { if (selectedMethod?.type !== 'mobile_money') setSelectedMethod({ id: '', type: 'mobile_money', value: momoPhone }) }}
							className="flex items-center gap-3 p-3 w-full"
						>
							<div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${selectedMethod?.type === 'mobile_money' ? 'bg-[#034a47]' : 'border-2 border-gray-300'}`}>
								{selectedMethod?.type === 'mobile_money' && <Check size={10} color="#fff" />}
							</div>
							<p className="text-xs font-semibold text-gray-800 flex-1 text-left">Mobile Money</p>
							<Image useBase width={60} height={16} alt="momo" src="/img/momo.svg" className="object-contain" />
						</button>
						{selectedMethod?.type === 'mobile_money' && (
							<div className="px-3 pb-3 flex flex-col gap-3">
								<div className="grid grid-cols-2 gap-2">
									{MOMO_PROVIDERS.map((p) => {
										const isSel = selectedMethod.id === p.id
										return (
											<button
												key={p.id}
												onClick={() => setSelectedMethod({ id: p.id, type: 'mobile_money', value: momoPhone })}
												className={`relative p-2.5 border-2 rounded-xl flex flex-col items-center gap-1.5 ${isSel ? 'border-[#034a47] bg-[#f0faf9]' : 'border-gray-200'}`}
											>
												{isSel && (
													<div className="absolute top-1.5 right-1.5 w-3.5 h-3.5 rounded-full bg-[#034a47] flex items-center justify-center">
														<Check size={8} color="#fff" />
													</div>
												)}
												<img src={p.logo} alt={p.label} className="h-5 w-full object-contain" />
												<span className="text-[10px] font-medium text-gray-600">{p.label}</span>
											</button>
										)
									})}
								</div>
								<div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 border-gray-200 focus-within:border-[#034a47]">
									<Phone size={13} color="#9CA3AF" className="flex-shrink-0" />
									<input
										type="tel"
										value={momoPhone}
										onChange={(e) => {
											setMomoPhone(e.target.value)
											if (selectedMethod?.type === 'mobile_money') setSelectedMethod({ ...selectedMethod, value: e.target.value })
										}}
										placeholder="0241234567"
										className="flex-1 text-xs text-gray-800 placeholder-gray-300 outline-none"
									/>
								</div>
							</div>
						)}
					</div>

					{/* Bank Card */}
					<button
						onClick={() => setSelectedMethod({ id: 'card', type: 'card' })}
						className={`flex items-center gap-3 p-3 border-2 rounded-xl transition-all ${selectedMethod?.id === 'card' ? 'border-[#034a47] bg-[#f0faf9]' : 'border-gray-200'}`}
					>
						<div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${selectedMethod?.id === 'card' ? 'bg-[#034a47]' : 'border-2 border-gray-300'}`}>
							{selectedMethod?.id === 'card' && <Check size={10} color="#fff" />}
						</div>
						<p className="text-xs font-semibold text-gray-800 flex-1 text-left">Bank Card</p>
						<Image useBase width={50} height={20} alt="cards" src="/img/cards.png" className="object-contain" />
					</button>

					{/* Saved cards collapsible */}
					{PaymentMethods.length > 0 && (
						<div>
							<button onClick={() => setSavedCardsOpen((o) => !o)} className="flex items-center gap-2 text-xs text-[#034a47] font-semibold py-1">
								<CreditCard size={13} />
								{savedCardsOpen ? 'Hide' : 'Show'} saved cards ({PaymentMethods.length})
							</button>
							{savedCardsOpen && (
								<div className="flex flex-col gap-2 mt-2">
									{PaymentMethods.map((method) => {
										const isSel = selectedMethod?.id === method.id
										return (
											<button
												key={method.id}
												onClick={() => setSelectedMethod({ id: method.id, type: 'card' })}
												className={`flex items-center gap-3 p-3 border-2 rounded-xl ${isSel ? 'border-[#034a47] bg-[#f0faf9]' : 'border-gray-200'}`}
											>
												<div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${isSel ? 'bg-[#034a47]' : 'border-2 border-gray-300'}`}>
													{isSel && <Check size={10} color="#fff" />}
												</div>
												<CreditCard size={13} color="#9CA3AF" className="flex-shrink-0" />
												<div className="flex-1 text-left">
													<p className="text-xs font-medium text-gray-800">•••• {method.last4}</p>
													<p className="text-[10px] text-gray-400 capitalize">{method.card_type} · {method.exp_month}/{method.exp_year}</p>
												</div>
											</button>
										)
									})}
								</div>
							)}
						</div>
					)}
				</div>
			)}

			{/* Summary + confirm */}
			<div className="px-2 pt-2 w-full border-t-2 border-gray-50">
				<div className="flex flex-row items-end justify-between">
					<div className="w-1/2">
						<p className="text-sm font-bold text-primary">
							₵{formatNumberScale(orderAmount + (activeFacility?.paymentConfig?.accepts_payment_on_delivery ? 0 : 15))}
						</p>
						<p className="text-xs font-bold">
							{bookingDateTime ? convertDateToString(new Date(bookingDateTime), true) : 'Select a time'}
						</p>
						<p className="text-[10px] text-gray-400 font-light">Free cancellation*</p>
					</div>
					<div className="w-1/2 pl-3">
						<button
							className={`flex items-center justify-center w-full py-3 rounded-md ${locationId && bookingDateTime ? 'bg-primary' : 'bg-gray-300'}`}
							disabled={loading || !locationId || !bookingDateTime}
							onClick={continueFunc}
						>
							{loading ? <RotatingLines width="20" strokeColor={DDGColors.gainsboro} /> : <p className="text-xs font-semibold text-white">Confirm Booking</p>}
						</button>
					</div>
				</div>
			</div>
		</div>
	)
}

export default ServiceBookingReview
